import type { DateRange, AttributionFilterState } from '@/components/analytics';
import type { CampaignFilterState } from '@/components/analytics/CampaignFilters';

import { useTranslation } from 'react-i18next';
import { useMemo, useState, useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/context/organization-context';
import { CampaignFilters } from '@/components/analytics/CampaignFilters';
import { CampaignComparison } from '@/components/analytics/CampaignComparison';
import { CampaignInsights } from '@/sections/campaign-audit/campaign-audit-view';
import { CampaignPreviewDrawer } from '@/components/analytics/CampaignPreviewDrawer';
import { CampaignPerformersGrid } from '@/components/analytics/CampaignPerformersGrid';
import { MetricCard, PeriodSelector, TimeSeriesChart, AttributionControl, useDefaultDateRange, SendTimePerformance } from '@/components/analytics';
import {
  useCampaignAnalyticsControllerGetCampaignList,
  useCampaignAnalyticsControllerGetCampaignOverview,
  useCampaignAnalyticsControllerGetCampaignEngagement,
} from '@/apis/backend/generated/analytics-campaigns/analytics-campaigns';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/analytics/campaigns')({
  component: CampaignsAnalyticsPage,
});

function CampaignsAnalyticsPage() {
  const { t } = useTranslation('analytics');
  const theme = useTheme();
  const { activeOrganization, activeClient } = useOrganization();
  const defaultRange = useDefaultDateRange(365);
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);
  const [attribution, setAttribution] = useState<AttributionFilterState>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('sendTime');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<CampaignFilterState>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showComparison, setShowComparison] = useState(false);
  const [previewCampaignId, setPreviewCampaignId] = useState<string | null>(null);
  const [campaignTab, setCampaignTab] = useState(0);

  const orgId = activeOrganization?.id;
  const clientId = activeClient?.id;

  // Auto-pick granularity based on date range span
  const granularity = useMemo(() => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (days > 90) return 'month' as const;
    if (days > 21) return 'week' as const;
    return 'day' as const;
  }, [dateRange.startDate, dateRange.endDate]);

  const formatXAxis = useCallback((dateStr: string) => {
    const d = new Date(dateStr);
    if (granularity === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }, [granularity]);

  const handleFiltersChange = useCallback((newFilters: CampaignFilterState) => {
    setFilters(newFilters);
    setPage(0);
  }, []);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 3) {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setShowComparison(false);
  }, []);

  const { data: overview, isLoading: overviewLoading } =
    useCampaignAnalyticsControllerGetCampaignOverview(
      {
        orgId: orgId!,
        clientId: clientId!,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        previousStartDate: dateRange.previousStartDate,
        previousEndDate: dateRange.previousEndDate,
        granularity,
        ...attribution,
      },
      { query: { enabled: !!orgId && !!clientId, staleTime: 60000 } }
    );

  const { data: engagement } =
    useCampaignAnalyticsControllerGetCampaignEngagement(
      {
        orgId: orgId!,
        clientId: clientId!,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        granularity,
      },
      { query: { enabled: !!orgId && !!clientId, staleTime: 60000 } }
    );

  const { data: campaignList, isLoading: listLoading } =
    useCampaignAnalyticsControllerGetCampaignList(
      {
        orgId: orgId!,
        clientId: clientId!,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: page + 1,
        pageSize,
        sortBy,
        sortOrder,
        ...(filters.nameKeyword ? { nameKeyword: filters.nameKeyword } : {}),
        ...(filters.nameExclude ? { nameExclude: filters.nameExclude } : {}),
        ...(filters.recipientsMin != null ? { recipientsMin: filters.recipientsMin } : {}),
        ...(filters.recipientsMax != null ? { recipientsMax: filters.recipientsMax } : {}),
        ...(filters.openRatePercentileMin != null ? { openRatePercentileMin: filters.openRatePercentileMin } : {}),
        ...(filters.openRatePercentileMax != null ? { openRatePercentileMax: filters.openRatePercentileMax } : {}),
        ...(filters.clickRatePercentileMin != null ? { clickRatePercentileMin: filters.clickRatePercentileMin } : {}),
        ...(filters.clickRatePercentileMax != null ? { clickRatePercentileMax: filters.clickRatePercentileMax } : {}),
        ...(filters.revenuePerRecipientPercentileMin != null ? { revenuePerRecipientPercentileMin: filters.revenuePerRecipientPercentileMin } : {}),
        ...(filters.revenuePerRecipientPercentileMax != null ? { revenuePerRecipientPercentileMax: filters.revenuePerRecipientPercentileMax } : {}),
        ...(filters.includeTags?.length ? { includeTags: filters.includeTags.join(',') } : {}),
        ...(filters.excludeTags?.length ? { excludeTags: filters.excludeTags.join(',') } : {}),
        ...(filters.channels?.length ? { channels: filters.channels.join(',') } : {}),
        ...attribution,
      },
      { query: { enabled: !!orgId && !!clientId, staleTime: 60000 } }
    );

  const selectedCampaigns = useMemo(
    () => (campaignList?.items ?? []).filter((c) => selectedIds.has(c.id)),
    [campaignList, selectedIds],
  );

  const kpis = overview?.kpis;
  const kpiCards = kpis
    ? [
        { title: t('campaigns.kpi.totalRevenue', 'Total Revenue'), metric: kpis.totalRevenue, format: 'currency' as const },
        { title: t('campaigns.kpi.totalDeliveries', 'Total Deliveries'), metric: kpis.totalDeliveries, format: 'number' as const },
        { title: t('campaigns.kpi.avgOpenRate', 'Avg Open Rate'), metric: kpis.avgOpenRate, format: 'percent' as const },
        { title: t('campaigns.kpi.avgClickRate', 'Avg Click Rate'), metric: kpis.avgClickRate, format: 'percent' as const },
      ]
    : [];

  if (!orgId || !clientId) return null;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const columns = [
    { id: 'name', label: t('campaigns.columns.name', 'Campaign') },
    { id: 'sendTime', label: t('campaigns.columns.sendTime', 'Sent') },
    { id: 'recipients', label: t('campaigns.columns.recipients', 'Recipients') },
    { id: 'openRate', label: t('campaigns.columns.openRate', 'Open Rate') },
    { id: 'clickRate', label: t('campaigns.columns.clickRate', 'Click Rate') },
    { id: 'bounceRate', label: t('campaigns.columns.bounceRate', 'Bounce Rate') },
    { id: 'unsubscribeRate', label: t('campaigns.columns.unsubscribeRate', 'Unsub Rate') },
    { id: 'revenue', label: t('campaigns.columns.revenue', 'Revenue') },
  ];

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
        <PeriodSelector value={dateRange} onChange={setDateRange} />
        <AttributionControl value={attribution} onChange={setAttribution} />
      </Stack>

      <Tabs
        value={campaignTab}
        onChange={(_, v) => setCampaignTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t('campaigns.tabs.overview', 'Overview')} sx={{ textTransform: 'none' }} />
        <Tab label={t('campaigns.tabs.insights', 'Insights')} sx={{ textTransform: 'none' }} />
      </Tabs>

      {campaignTab === 1 && orgId && clientId && (
        <Stack spacing={2.5}>
          <CampaignInsights orgId={orgId} clientId={clientId} />
          <CampaignPerformersGrid
            orgId={orgId}
            clientId={clientId}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            attribution={attribution}
          />
        </Stack>
      )}

      {campaignTab === 0 && (
      <>
      {/* KPI Cards */}
      {overviewLoading ? (
        <Stack direction="row" spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={100} sx={{ flex: 1, borderRadius: '12px' }} />
          ))}
        </Stack>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          {kpiCards.map((card) => (
            <MetricCard key={card.title} {...card} />
          ))}
        </Box>
      )}

      {/* Charts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
        {overview?.timeSeries && overview.timeSeries.length > 0 && (
          <TimeSeriesChart
            title={t('campaigns.charts.performance.title', 'Campaign Performance')}
            data={overview.timeSeries}
            series={[
              { dataKey: 'revenue', name: t('campaigns.charts.performance.revenue', 'Email Campaign Revenue'), type: 'bar', color: theme.palette.success.light, yAxisId: 'left' },
              { dataKey: 'deliveries', name: t('campaigns.charts.performance.deliveries', 'Email Campaign Deliveries'), type: 'line', color: theme.palette.success.dark, yAxisId: 'right', dot: true },
            ]}
            formatXAxis={formatXAxis}
            formatLeftAxis={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toLocaleString()}`}
            formatRightAxis={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toLocaleString()}
            formatTooltip={(value, name) =>
              name === t('campaigns.charts.performance.revenue', 'Email Campaign Revenue')
                ? `$${value.toLocaleString()}`
                : value.toLocaleString()
            }
          />
        )}
        {engagement?.timeSeries && engagement.timeSeries.length > 0 && (
          <TimeSeriesChart
            title={t('campaigns.charts.engagement.title', 'Engagement Rates')}
            data={engagement.timeSeries}
            series={[
              { dataKey: 'openRate', name: t('campaigns.columns.openRate', 'Open Rate'), type: 'line', color: theme.palette.primary.main, yAxisId: 'left' },
              { dataKey: 'clickRate', name: t('campaigns.columns.clickRate', 'Click Rate'), type: 'line', color: theme.palette.warning.main, yAxisId: 'right' },
            ]}
            formatXAxis={formatXAxis}
            formatLeftAxis={(v) => `${v.toFixed(0)}%`}
            formatRightAxis={(v) => `${v.toFixed(1)}%`}
            formatTooltip={(value) => `${value.toFixed(1)}%`}
          />
        )}
      </Box>

      {/* Send Time Performance */}
      <SendTimePerformance
        clientId={clientId}
        orgId={orgId}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        filters={filters}
      />

      {/* Campaign Comparison */}
      {showComparison && selectedCampaigns.length >= 2 && (
        <CampaignComparison campaigns={selectedCampaigns} onClose={() => setShowComparison(false)} />
      )}

      {/* Campaign Filters + Table */}
      <Card sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'grey.200' }}>
        <Stack spacing={2} sx={{ p: 2, pb: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('campaigns.deepDive.heading', 'Campaign Deep Dive')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {selectedIds.size > 0 && (
                <Button size="small" variant="text" onClick={clearSelection}>
                  {t('campaigns.deepDive.clearSelection', 'Clear ({{count}})', { count: selectedIds.size })}
                </Button>
              )}
              <Badge badgeContent={selectedIds.size} color="primary">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CompareArrowsIcon />}
                  disabled={selectedIds.size < 2}
                  onClick={() => setShowComparison(true)}
                >
                  {t('campaigns.deepDive.compare', 'Compare')}
                </Button>
              </Badge>
            </Stack>
          </Stack>
          <CampaignFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            clientId={clientId}
            orgId={orgId}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        </Stack>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }} />
                {columns.map((col) => (
                  <TableCell key={col.id}>
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortOrder : 'desc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell padding="checkbox"><Skeleton width={20} /></TableCell>
                    {columns.map((col) => (
                      <TableCell key={col.id}>
                        <Skeleton width={80} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : campaignList?.items?.length ? (
                campaignList.items.map((campaign) => {
                  const isSelected = selectedIds.has(campaign.id);
                  const isDisabled = !isSelected && selectedIds.size >= 3;
                  return (
                    <TableRow
                      key={campaign.id}
                      hover
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => { if (!isDisabled) toggleSelection(campaign.id); }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => { if (!isDisabled) toggleSelection(campaign.id); }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            maxWidth: 200,
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewCampaignId(campaign.id);
                          }}
                        >
                          {campaign.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {campaign.sendTime
                          ? new Date(campaign.sendTime).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>{campaign.recipients?.toLocaleString() || 0}</TableCell>
                      <TableCell>{campaign.openRate?.toFixed(1)}%</TableCell>
                      <TableCell>{campaign.clickRate?.toFixed(1)}%</TableCell>
                      <TableCell>{campaign.bounceRate?.toFixed(1)}%</TableCell>
                      <TableCell>{campaign.unsubscribeRate?.toFixed(1)}%</TableCell>
                      <TableCell>${parseFloat(String(campaign.revenue || 0)).toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      {t('campaigns.empty.noCampaigns', 'No campaigns found for this period.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {campaignList && (
          <TablePagination
            component="div"
            count={campaignList.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </Card>
      </>
      )}

      <CampaignPreviewDrawer
        campaignId={previewCampaignId}
        onClose={() => setPreviewCampaignId(null)}
        attribution={attribution}
      />
    </Stack>
  );
}
